/** @jsx isolateComponent */
import { useState } from 'react';

import { Animation, Colors, Link, Shadows } from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

// Copy/pasted from `design-system` because flowgen bug
export type AnchorTarget = '_self' | '_blank' | '_parent' | '_top';

type ListItemProps = {
  /** Content of list item */
  children: React.ReactNode;
  /** used to render as a Link */
  href?: string;
  /** true to supress focus / hover styles */
  isReadOnly?: boolean;
  /** size to render */
  size: 'small' | 'medium' | 'large';
  /** Anchor target if is link */
  target?: AnchorTarget;
};

const Styles = labelStyles(__filename, {
  root: (isFocused: boolean, size: ListItemProps['size']) => [
    {
      backgroundColor: Colors.grey000,
      border: `1px solid transparent`,
      borderRadius: 10,
      padding: '15px 20px',
      transition: Animation.transitionValue,
    },
    isFocused &&
      withModifier('isFocused', {
        backgroundColor: Colors.white,
        border: `1px solid ${Colors.grey200}`,
        borderRadius: 10,
        boxShadow: Shadows.elevation5,
      }),
    size === 'small' &&
      withModifier('small', {
        padding: 15,
      }),
  ],
});

const ListItem: React.FC<ListItemProps> = props => {
  const [isFocused, setIsFocused] = useState(false);

  const focusHandlers = props.isReadOnly
    ? {}
    : {
        onBlur: () => setIsFocused(false),
        onFocus: () => setIsFocused(true),
        onMouseEnter: () => setIsFocused(true),
        onMouseLeave: () => setIsFocused(false),
      };

  const item = (
    <article
      css={Styles.root(isFocused, props.size)}
      {...(props.href ? {} : focusHandlers)}
    >
      {props.children}
    </article>
  );

  return props.href ? (
    <Link href={props.href} target={props.target} {...focusHandlers}>
      {item}
    </Link>
  ) : (
    item
  );
};

ListItem.defaultProps = {
  isReadOnly: false,
};

export default ListItem;
